import React from 'react';

import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import DatePicker from 'react-multi-date-picker';

import {deCalendar} from '../../../../utils/deCalendar';
import {Info, KalendarSecondary} from '../../../icons';
import {Label, P} from '../../../layout/Text';
import {InfoPopup} from '../../InfoPopup';
import {Hints} from '../Hints';

import {useRangeDatePicker} from './useRangeDatePicker';
import {
    getBaseVariables,
    getDisabledVariables,
    getErrorVariables,
    getFocusVariables
} from './utils';

import type {ValidationFunc} from '@nfq/react-form';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
export interface ComponentProps {
    /**
     * An array of hint messages to display below the dropdown input.
     * These hints provide additional context or guidance to the user about the expected input
     * or usage of the dropdown component.
     *
     * @default undefined
     */
    hints?: string[];
    /**
     * The initial value of the input field.
     * This value is set when the input is first rendered.
     *
     * @default ''
     */
    initialValue?: Date[];
    /**
     * Indicates whether to retain the field value when the component unmounts.
     *
     * @default false
     */
    keepOnUnmount?: boolean;
    /**
     * The label for the input field.
     * This label is displayed above the input element.
     */
    label?: string;
    /**
     * The name attribute for the input field.
     * It identifies the input element in form submissions and the form state.
     */
    name: string;
    /**
     * Callback function triggered when the input value changes.
     */
    onChange?(value: [Date, Date]): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'RangeDatePicker'
     */
    testId?: string;
    /**
     * Optional tooltip text to display an info icon next to the label.
     */
    tooltipText?: string;
    /**
     * Optional validation function to validate the input value.
     */
    validation?: ValidationFunc;
}

/**
 * RangeDatePicker is a React component that provides a date range selection interface using a calendar picker.
 * This component integrates with a form system and provides validation, error handling, and customizable styling.
 * It allows users to select a start and end date from an interactive calendar with range highlighting and hover effects.
 *
 * @param props               The component props.
 * @param props.hints         An array of hint messages to display below the date picker input.
 * @param props.initialValue  The initial date range value as an array of two Date objects.
 * @param props.keepOnUnmount Whether to retain the field value when the component unmounts.
 * @param props.label         The label text to display above the date picker input.
 * @param props.name          The name attribute for the input field, used for form identification.
 * @param props.onChange      Callback function triggered when the date range selection changes.
 * @param props.testId        Unique identifier for testing purposes.
 * @param props.tooltipText   Optional tooltip text to display an info icon next to the label.
 * @param props.validation    Optional validation function to validate the selected date range.
 * @returns A JSX element representing the range date picker component.
 *
 * @example
 * ```tsx
 * <RangeDatePicker
 *   name="dateRange"
 *   label="Select Date Range"
 *   initialValue={[new Date(), new Date()]}
 *   onChange={(range) => console.log('Selected range:', range)}
 *   hints={['Select your preferred date range']}
 *   testId="booking-date-picker"
 * />
 * ```
 */
const RangeDatePicker = observer(({
    hints = [],
    initialValue = [],
    keepOnUnmount = false,
    label,
    name,
    onChange = () => {},
    testId = 'RangeDatePicker',
    tooltipText,
    validation = () => null
}: ComponentProps) => {
    const {
        colors,
        error,
        handleChange,
        value
    } = useRangeDatePicker(name, initialValue, keepOnUnmount, onChange, validation);

    return (
        <Wrapper>
            <StyledLabel>
                {label && (
                    <>
                        <LabelInfoButtonWrapper>
                            <InputLabel data-cy={`${testId}-label`}>{label}</InputLabel>
                            {tooltipText && (
                                <InfoPopup testId={`${testId}-info-popup`}>
                                    <P $size="small" data-cy={`${testId}-info-text`}>{tooltipText}</P>
                                </InfoPopup>
                            )}
                        </LabelInfoButtonWrapper>
                        <Spacer y={2} />
                    </>
                )}
                <InputIconWrapper>
                    <Picker
                        dateSeparator=" - "
                        format="DD.MM.YYYY"
                        locale={deCalendar}
                        numberOfMonths={2}
                        shadow={false}
                        value={value}
                        weekStartDayIndex={1}
                        range
                        rangeHover
                        onChange={handleChange}
                    />
                    <ErrorIcon $show={(error !== null && error.length > 0)} color1={colors.error} testId={`${testId}-error-icon`} />
                    <KalenderIcon $show={(error === null || error.length <= 0)} testId={`${testId}-calendar-icon`} />
                </InputIconWrapper>
            </StyledLabel>
            {(error !== null && error.length > 0) && (
                <Hints baseTestId={testId} hints={error} hintType="error" paddingLeft={0} />
            )}
            {(error === null || error.length === 0) && <Hints baseTestId={testId} hints={hints} paddingLeft={0} />}
        </Wrapper>
    );
});

RangeDatePicker.displayName = 'RangeDatePicker';

export {RangeDatePicker};

const Wrapper = styled.div`
    ${getBaseVariables};

    &:has(input:focus-visible) {
        ${getFocusVariables};
    }

    &:has(input[data-has-error=true]) {
        ${getErrorVariables}
    }

    &:has(input[disabled]) {
        ${getDisabledVariables};
    }

    .rmdp-container {
        height: 100% !important;
        width: 100%;

        .rmdp-input {
            background-color: var(--text-input-background-color);
            border: 1px solid var(--text-input-border-color);
            border-radius: 4px;
            color: var(--text-input-font-color);
            font-family: ${({theme}) => theme.fonts.OnAir};
            font-size: 1.6rem;
            height: 100%;
            line-height: 2.4rem;
            padding: ${spacing(3)} ${spacing(4)};
            transition: background-color 0.3s ease-in-out, border-color 0.3s ease-in-out;
            width: 100%;

            &:focus {
                outline: none !important;
            }
            &:placeholder {
                color: var(--text-input-placeholder-font-color);
            }

            &[disabled] {
                cursor: not-allowed;
            }
        }
    }
`;

const StyledLabel = styled(Label)`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const LabelInfoButtonWrapper = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(2)};
`;

const InputLabel = styled(P)`
    color: var(--label-font-color);
    transition: color 0.3s ease-in-out;
`;

const InputIconWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const Picker = styled(DatePicker)`
    font-family: ${({theme}) => theme.fonts.OnAir};

    .rmdp-arrow-container {
        align-items: center;
    }
    .rmdp-arrow {
        margin-top: 0;
    }
`;

interface IconProps {
    $show: boolean;
}

const ErrorIcon = styled(Info)<IconProps>`
    opacity: ${({$show}) => ($show ? 1 : 0)};
    position: absolute;
    right: ${spacing(4)};
    top: 50%;
    transform: translateY(-50%);
    transition: opacity 0.3s ease-in-out;
`;

const KalenderIcon = styled(KalendarSecondary)<IconProps>`
    opacity: ${({$show}) => ($show ? 1 : 0)};
    position: absolute;
    right: ${spacing(4)};
    top: 50%;
    transform: translateY(-50%);
    transition: opacity 0.3s ease-in-out;
`;